import React, { useState } from "react";
import { Heart, Coffee } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CONFIG = {
  heart: { Icon: Heart, label: "Îmi place", active: "bg-[var(--tint-pink)] text-[var(--accent)] ring-[color-mix(in_srgb,var(--accent)_40%,transparent)]", fill: "#B5121B" },
  coffee: { Icon: Coffee, label: "O cafea", active: "bg-[var(--tint-butter)] text-[var(--sun-dark)] ring-[color-mix(in_srgb,var(--sun)_50%,transparent)]", fill: "#B8865B" },
};

const readLocal = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(`reactions:${slug}`)) || {};
  } catch {
    return {};
  }
};

export const ReactionBar = ({ slug, initial }) => {
  const [counts, setCounts] = useState({ heart: 0, coffee: 0, ...(initial || {}) });
  const [mine, setMine] = useState(() => readLocal(slug));
  const [busy, setBusy] = useState(false);
  const [askNameFor, setAskNameFor] = useState(null); // reaction type awaiting optional name
  const [name, setName] = useState("");

  const toggle = async (type) => {
    if (busy) return;
    setBusy(true);
    const isActive = !!mine[type];
    const nextMine = { ...mine, [type]: !isActive };
    setMine(nextMine);
    localStorage.setItem(`reactions:${slug}`, JSON.stringify(nextMine));
    setCounts((c) => ({ ...c, [type]: Math.max(0, (c[type] || 0) + (isActive ? -1 : 1)) }));
    try {
      const { data } = await api.post(`/posts/${slug}/reactions`, { type, action: isActive ? "remove" : "add" });
      setCounts((c) => ({ ...c, ...data.reactions }));
      setAskNameFor(isActive ? null : type);
    } catch {
      setMine(mine);
      localStorage.setItem(`reactions:${slug}`, JSON.stringify(mine));
      setCounts((c) => ({ ...c, [type]: Math.max(0, (c[type] || 0) + (isActive ? 1 : -1)) }));
    } finally {
      setBusy(false);
    }
  };

  const sendName = async (e) => {
    e.preventDefault();
    if (!name.trim() || !askNameFor) return;
    try {
      await api.post(`/posts/${slug}/reactions/name`, { type: askNameFor, name: name.trim() });
      toast.success("Mulțumesc! Alexia va ști de la cine e aprecierea.");
    } catch {
      toast.error("Nu am putut trimite numele, dar aprecierea a rămas.");
    }
    setAskNameFor(null);
    setName("");
  };

  return (
    <div data-testid="reaction-bar">
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/62">Ți-a plăcut?</span>
        {Object.entries(CONFIG).map(([type, { Icon, label, active, fill }]) => {
          const on = !!mine[type];
          return (
            <button
              key={type}
              data-testid={`reaction-${type}`}
              onClick={() => toggle(type)}
              aria-pressed={on}
              className={`group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ring-1 transition-all active:scale-90 ${
                on ? `${active} ring-2` : "bg-[#2B1B13] text-[#F7F1E8]/86 ring-[color-mix(in_srgb,var(--milk)_15%,transparent)] hover:ring-[color-mix(in_srgb,var(--milk)_35%,transparent)]"
              }`}
            >
              <Icon
                className="h-4 w-4 transition-transform group-hover:scale-110"
                strokeWidth={1.8}
                fill={on ? fill : "none"}
              />
              {label}
              <span data-testid={`reaction-${type}-count`} className="font-semibold tabular-nums">
                {counts[type] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {askNameFor && (
        <form
          onSubmit={sendName}
          data-testid="reaction-name-form"
          className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl bg-[color-mix(in_srgb,var(--tint-pink)_60%,transparent)] px-4 py-3"
        >
          <span className="text-sm text-[#F7F1E8]/86">Mulțumesc! Vrei să-ți lași și numele? (opțional)</span>
          <input
            data-testid="reaction-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="numele tău"
            className="min-w-[140px] flex-1 rounded-full border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
          <button
            type="submit"
            data-testid="reaction-name-submit"
            className="rounded-full bg-[var(--accent)] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-white hover:bg-[#7E0C13]"
          >
            Trimite
          </button>
          <button
            type="button"
            data-testid="reaction-name-skip"
            onClick={() => { setAskNameFor(null); setName(""); }}
            className="text-xs text-[#F7F1E8]/68 underline underline-offset-2 hover:text-[#F7F1E8]"
          >
            Nu, mulțumesc
          </button>
        </form>
      )}
    </div>
  );
};

export default ReactionBar;
